import { useDispatch, useSelector } from "react-redux";
import React, { useEffect, useState, useCallback, useRef } from "react";
import SettingsInputSvideoIcon from "@material-ui/icons/SettingsInputSvideo";
import RegularButton from "components/CustomButtons/Button";
import { animations } from "helpers/animations";
import {
  getItemLocalStorage,
  getPersistLocalStorage,
} from "helpers/localstorageTool";
import { green, blue, red } from "@material-ui/core/colors";
import PhoneInTalkIcon from "@material-ui/icons/PhoneInTalk";
import amazonConnectSVG from "../../assets/icons/svg/amazon-connect.svg";
import { Paper, Typography } from "@material-ui/core";
import {
  handleOpenAmazonConnect,
  getAmazonInfoCall,
  restoreCallID,
} from "redux/actions/customers/customersAction";
import { formatTime } from "hooks/useTimer";

const boxStyle = {
  width: 360,
  height: 625,
  borderRadius: 8,
  zIndex: 1,
  padding: 0,
};

const panelStyle = {
  display: "flex",
  alignItems: "center",
  borderRadius: 16,
  backgroundColor: "white",
  padding: "0 16px",
};

const connectHTML = `
<!DOCTYPE html>
<html>
<head>
  <title>CCP Embeded into custom HTML</title>
  <meta charset="UTF-8">
  <script type="text/javascript" src="/lib/connect-streams-min.js"></script>
</head>
<body>
  <div id="container-div" style="float:left; width: 100%; height: 600px; border-radius: 8px;"></div>
  <script type="module" src="/scripts/index.js?v=2"></script>
</body>
</html>
`;

export const AmazonConnectPanel = () => {
  const urlBalancer = process.env.REACT_APP_AMAZON_CONNECT;
  const dispatch = useDispatch();
  const { openAmazonConnect, amazonConnectCall } = useSelector(
    ({ customersReducer }) => customersReducer,
  );

  const [style, setStyle] = useState(panelStyle);
  const [agentState, setAgentState] = useState(
    getItemLocalStorage("agent-state") || "offline",
  );
  const [agentCall, setAgentCall] = useState(
    getPersistLocalStorage("contact-data") || {},
  );
  const [seconds, setSeconds] = useState(() => {
    try {
      const contactData = getPersistLocalStorage("contact-data") || {};
      if (contactData?.callID) {
        const savedStartTime = localStorage.getItem(`call-start-${contactData.callID}`);
        if (savedStartTime) {
          const startTime = parseInt(savedStartTime);
          if (!isNaN(startTime)) {
            return Math.floor((Date.now() - startTime) / 1000);
          }
        }
      }
    } catch (error) {
      console.error('Error initializing seconds from localStorage:', error);
    }
    return 0;
  });
  const [running, setRunning] = useState(false);
  const [activeCallId, setActiveCallId] = useState(() => {
    try {
      const contactData = getPersistLocalStorage("contact-data") || {};
      return contactData?.callID || null;
    } catch (error) {
      console.error('Error initializing activeCallId from localStorage:', error);
      return null;
    }
  });
  const [callStartTime, setCallStartTime] = useState(() => {
    try {
      const contactData = getPersistLocalStorage("contact-data") || {};
      if (contactData?.callID) {
        const savedStartTime = localStorage.getItem(`call-start-${contactData.callID}`);
        if (savedStartTime) {
          const startTime = parseInt(savedStartTime);
          if (!isNaN(startTime)) return startTime;
        }
      }
    } catch (error) {
      console.error('Error initializing callStartTime from localStorage:', error);
    }
    return null;
  });

  const activeCallIdRef = useRef(activeCallId);

  const updateFromLocalStorage = useCallback(() => {
    const newAgentState = getItemLocalStorage("agent-state");
    const newAgentCall = getPersistLocalStorage("contact-data") || {};

    if (newAgentState !== agentState) {
      setAgentState(newAgentState || "offline");
    }

    if (JSON.stringify(newAgentCall) !== JSON.stringify(agentCall)) {
      if (newAgentCall?.callID) {
        setAgentCall(newAgentCall);
        dispatch(getAmazonInfoCall(newAgentCall));
      } else if (activeCallIdRef.current) {
        localStorage.removeItem(`call-start-${activeCallIdRef.current}`);
        dispatch(restoreCallID());
        setRunning(false);
        setSeconds(0);
        setActiveCallId(null);
        setCallStartTime(null);
        activeCallIdRef.current = null;
        setAgentCall({});
      }
    }
  }, [agentState, agentCall, dispatch]);

  const updateBorderStyle = useCallback(() => {
    if (agentState === "available" && amazonConnectCall?.phone) {
      setStyle((prevStyle) => ({
        ...prevStyle,
        boxShadow: `0 0 10px -2px ${green[500]}`,
      }));
    } else if (agentState === "available" && !amazonConnectCall?.phone) {
      setStyle((prevStyle) => ({
        ...prevStyle,
        boxShadow: `0 0 10px -2px ${blue[500]}`,
      }));
    } else {
      setStyle((prevStyle) => ({
        ...prevStyle,
        boxShadow: `0 0 10px -2px ${red[500]}`,
      }));
    }
  }, [agentState, amazonConnectCall]);
  useEffect(() => {
    const contactData = getPersistLocalStorage("contact-data") || {};
    if (contactData?.callID) {
      dispatch(getAmazonInfoCall(contactData));
      const savedStartTime = localStorage.getItem(`call-start-${contactData.callID}`);
      if (savedStartTime) {
        setRunning(true);
      }
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    updateFromLocalStorage();

    const handleStorageChange = (e) => {
      if (e.key === "agent-state" || e.key === "contact-data") {
        updateFromLocalStorage();
      }
    };

    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'CALL_ENDED') {
        if (activeCallIdRef.current) {
          localStorage.removeItem(`call-start-${activeCallIdRef.current}`);
        }
        dispatch(restoreCallID());
        setRunning(false);
        setSeconds(0);
        setActiveCallId(null);
        setCallStartTime(null);
        activeCallIdRef.current = null;
      }
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("message", handleMessage);

    const intervalId = setInterval(updateFromLocalStorage, 1000);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("message", handleMessage);
      clearInterval(intervalId);
    };
  }, [updateFromLocalStorage, dispatch]);

  useEffect(() => {
    updateBorderStyle();

    if (amazonConnectCall?.phone) {
      dispatch(handleOpenAmazonConnect(true));
      
      if (amazonConnectCall.callID !== activeCallId) {
        const savedStartTime = localStorage.getItem(`call-start-${amazonConnectCall.callID}`);
        
        if (savedStartTime) {
          const startTime = parseInt(savedStartTime);
          setCallStartTime(startTime);
          setSeconds(Math.floor((Date.now() - startTime) / 1000));
        } else {
          const startTime = Date.now();
          setCallStartTime(startTime);
          setSeconds(0);
          localStorage.setItem(`call-start-${amazonConnectCall.callID}`, startTime.toString());
        }
        
        setActiveCallId(amazonConnectCall.callID);
        activeCallIdRef.current = amazonConnectCall.callID;
      }
      
      setRunning(true);
    } else {
      setRunning(false);
    }
  }, [
    agentState,
    amazonConnectCall,
    updateBorderStyle,
    dispatch,
    activeCallId,
  ]);

  useEffect(() => {
    let interval;
    if (running && callStartTime) {
      interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - callStartTime) / 1000);
        setSeconds(elapsed);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [running, callStartTime]);

  const handleLogin = () => {
    const centerX = window.innerWidth / 2;
    const centerY = window.innerHeight / 2;

    const popupWidth = 426;
    const popupHeight = 740;
    const left = centerX - popupWidth / 2;
    const top = centerY - popupHeight / 2;

    const windowOptions = `width=${popupWidth},height=${popupHeight},left=${left},top=${top}`;
    agentState === "offline" &&
      window.open(urlBalancer, "_blank", windowOptions);
  };

  const handleOpenConnect = () => {
    dispatch(handleOpenAmazonConnect(!openAmazonConnect));
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 16,
        height: 50,
        transition: "all 0.5s ease",
      }}
    >
      <Paper style={style}>
        {agentCall?.city && (
          <Typography variant="subtitle1" style={{ marginRight: 16 }}>
            {agentCall?.city}
          </Typography>
        )}
        {agentState === "offline" && (
          <RegularButton
            justIcon
            round
            color="transparent"
            tooltip="Iniciar Sesión Amazon Connect"
            onClick={handleLogin}
          >
            <SettingsInputSvideoIcon
              style={{
                color: agentState === "available" ? blue[300] : red[300],
              }}
            />
          </RegularButton>
        )}
        {amazonConnectCall?.logo && (
          <img
            src={amazonConnectCall?.logo}
            alt="logo"
            className={animations.fadeIn}
            style={{
              width: 120,
              height: 60,
              objectFit: "contain",
              borderRadius: 5,
            }}
          />
        )}
        <RegularButton
          tooltip={openAmazonConnect ? "Minimizar" : "Amazon Connect"}
          color="transparent"
          onClick={handleOpenConnect}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            margin: 0,
          }}
        >
          <img
            src={amazonConnectSVG}
            style={{
              width: 32,
            }}
          />
          {amazonConnectCall?.phone && (
            <>
              <span style={{ marginLeft: 8 }}>{amazonConnectCall?.phone}</span>
              <PhoneInTalkIcon
                className={animations.shake}
                style={{
                  color: green[300],
                  animationIterationCount: 5,
                  fontSize: 24,
                  marginLeft: 16,
                }}
              />
            </>
          )}
        </RegularButton>
        <Typography
          variant="h5"
          color={running ? "success.main" : "text.secondary"}
        >
          {formatTime(seconds)}
        </Typography>
      </Paper>

      <div
        style={{
          position: "absolute",
          left: "50%",
          bottom: 0,
          transform: "translateX(-50%) translateY(100%)",
          height: openAmazonConnect ? 625 : 0,
          opacity: openAmazonConnect ? 1 : 0,
          transition: "all .3s ease",
          overflow: "hidden",
        }}
      >
        {window === window.parent && (
          <iframe
            id="amazon--connect"
            name="amazon--connect"
            srcDoc={connectHTML}
            allow="fullscreen"
            style={{
              ...boxStyle,
              backgroundColor: "white",
              border: "none",
            }}
          />
        )}
      </div>
    </div>
  );
};
